import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ALL_PRODUCTS } from "../data/products";
import useReveal from "../hooks/useReveal";
import "./ProductDetail.css";

export default function ProductDetail() {
  const { id } = useParams();
  const product = ALL_PRODUCTS.find((p) => p.id === id);

  const [activeImg, setActiveImg] = useState(0);

  useReveal();

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImg(0);
  }, [id]);

  if (!product) {
    return (
      <main style={{ paddingTop: "70px", textAlign: "center", padding: "8rem 2rem" }}>
        <p style={{ fontSize: "3rem" }}>🔍</p>
        <h2 style={{ fontFamily: "var(--font-display)", color: "var(--navy)", marginTop: "1rem" }}>
          Product Not Found
        </h2>
        <Link to="/products" className="btn btn-primary" style={{ marginTop: "2rem", display: "inline-flex" }}>
          ← All Products
        </Link>
      </main>
    );
  }

  /* ── Safe string helper — object/array dono handle kare ── */
  function safeStr(val) {
    if (!val && val !== 0) return "";
    if (typeof val === "string") return val;
    if (typeof val === "number") return String(val);
    if (Array.isArray(val)) {
      return val
        .map((v) =>
          typeof v === "object" && v !== null
            ? `${v.size || ""}${v.mrp ? ` – ${v.mrp}` : ""}`
            : String(v)
        )
        .join(" / ");
    }
    if (typeof val === "object") {
      return val.size ? `${val.size}${val.mrp ? ` – ${val.mrp}` : ""}` : "";
    }
    return "";
  }

  const getParentCategory = (product) => {
    const text = `${safeStr(product.type)} ${product.name || ""}`.toLowerCase();
    if (text.includes("cone"))  return { label: "Dhoop Cone",  path: "/products/dhoop-cone" };
    if (text.includes("stick")) return { label: "Dhoop Stick", path: "/products/dhoop-stick" };
    return { label: "Agarbatti", path: "/products/agarbatti" };
  };

  const parent = getParentCategory(product);

  const buildImagesArray = (p) => {
    if (p.imgs && p.imgs.length > 0) return p.imgs;
    const collected = [];
    if (p.img1) collected.push(p.img1);
    if (p.img2) collected.push(p.img2);
    if (p.img3) collected.push(p.img3);
    if (p.img4) collected.push(p.img4);
    if (collected.length > 0) return collected;
    if (p.img) return [p.img];
    return ["/images/no-image.webp"];
  };

  const images = buildImagesArray(product);

  /* ── Packing rows — weight + packing dono se merge karke banao ──
     Data formats supported:
     weight: "100g / 250g"  |  ["100g","250g"]  |  [{size,mrp}]
     packing: ["100gm - MRP ₹80/-", "250gm - MRP ₹150/-"]
              or [{size,mrp}]
  ── */
  const buildPackingRows = () => {
    const rowMap = new Map(); // key = normalized size string

    // Step 1 — weight se sizes nikaalo
    const w = product.weight;
    if (w) {
      const arr = Array.isArray(w) ? w : [w];
      arr.forEach((item) => {
        if (typeof item === "object" && item !== null) {
          const key = (item.size || "").trim();
          rowMap.set(key, { size: key, mrp: item.mrp || "" });
        } else {
          // "100g / 250g" ya "100g" string split
          String(item).split("/").forEach((s) => {
            const key = s.trim();
            if (key) rowMap.set(key, { size: key, mrp: "" });
          });
        }
      });
    }

    // Step 2 — packing array se MRP merge / add karo
    const pk = product.packing;
    if (pk && Array.isArray(pk)) {
      pk.forEach((item) => {
        if (typeof item === "object" && item !== null) {
          // {size, mrp} format
          const key = (item.size || "").trim();
          rowMap.set(key, { size: key, mrp: item.mrp || "" });
        } else {
          // "100gm - MRP ₹80/-"  or  "100gm - ₹80"  or  "100gm ₹80"
          const str = String(item);
          // Try to split on " - " or " – "
          const sepMatch = str.match(/^(.+?)[\-–](.+)$/);
          if (sepMatch) {
            const size = sepMatch[1].trim();
            const mrpRaw = sepMatch[2].replace(/MRP\s*/i, "").trim();
            // Find existing row with matching size (fuzzy: "100g" vs "100gm")
            const existingKey = [...rowMap.keys()].find(
              (k) => k.replace(/[^0-9]/g, "") === size.replace(/[^0-9]/g, "")
            );
            const finalKey = existingKey || size;
            rowMap.set(finalKey, { size: finalKey, mrp: mrpRaw });
          } else {
            // No separator — treat whole string as size
            const key = str.trim();
            if (!rowMap.has(key)) rowMap.set(key, { size: key, mrp: "" });
          }
        }
      });
    }

    return [...rowMap.values()].filter((r) => r.size);
  };

  const packingRows = buildPackingRows();

  const related = ALL_PRODUCTS
    .filter((p) => p.type === product.type && p.tier === product.tier && p.id !== product.id)
    .slice(0, 3);

  return (
    <main style={{ paddingTop: "70px" }}>

      {/* ── BREADCRUMB ── */}
      <nav className="breadcrumb">
        <div className="container breadcrumb__inner">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to={parent.path}>{parent.label}</Link>
          <span>/</span>
          <span>{product.name}</span>
        </div>
      </nav>

      {/* ── MAIN PRODUCT SECTION ── */}
      <section className="section pd-section">
        <div className="container">
          <div className="pd-grid">

            {/* LEFT — images */}
            <div className="pd-images reveal-l">
              <div className="pd-main-img">
                <img src={images[activeImg]} alt={product.name} />
                {product.tier && <div className="pd-tier-tag">{product.tier}</div>}
              </div>

              {images.length > 1 && (
                <div className="pd-thumbs">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      className={`pd-thumb ${i === activeImg ? "pd-thumb--active" : ""}`}
                      onClick={() => setActiveImg(i)}
                      type="button"
                    >
                      <img src={img} alt={`${product.name} view ${i + 1}`} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* RIGHT — details */}
            <div className="pd-details reveal-r">
              <div className="eyebrow">
                {safeStr(product.type)}
                {product.fragrance ? ` • ${safeStr(product.fragrance)}` : ""}
              </div>

              <h1 className="h-display product-title">{product.name}</h1>

              <p className="h-sub">
                {product.shortDesc || product.longDesc || product.desc}
              </p>

              {/* ── SPECS GRID — 2 columns ── */}
              <div className="pd-specs pd-specs--2col">
                {[
                  ["⏱",  "Duration",  safeStr(product.duration || product.burnTime)],
                  ["🌸",  "Fragrance", safeStr(product.fragrance)],
                  ...(product.scent    ? [["✨", "Scent Type", safeStr(product.scent)]]    : []),
                  ...(product.material ? [["🪵", "Material",   safeStr(product.material)]] : []),
                ]
                  .filter(([,, val]) => val)
                  .map(([icon, label, val]) => (
                    <div key={label} className="pd-spec">
                      <span className="pd-spec-icon-wrap">{icon}</span>
                      <div className="pd-spec-text">
                        <strong>{label}</strong>
                        <p>{val}</p>
                      </div>
                    </div>
                  ))}
              </div>

              {/* ── PACKING TABLE — size + MRP clearly dikhao ── */}
              {packingRows.length > 0 && (
                <div className="pd-packing-block">
                  <h4 className="pd-packing-title">⚖️ Packing &amp; Price</h4>
                  <div className="pd-packing-table">
                    <div className="pd-packing-table__head">
                      <span>Pack Size</span>
                      <span>MRP</span>
                    </div>
                    {packingRows.map((row, i) => (
                      <div key={i} className="pd-packing-table__row">
                        <span>{row.size || "—"}</span>
                        <span className={row.mrp ? "pd-mrp" : ""}>{row.mrp || "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Highlights */}
              {product.highlights?.length > 0 && (
                <div className="pd-highlights">
                  {product.highlights.map((h, i) => (
                    <span key={i} className="pd-hl">✓ {h}</span>
                  ))}
                </div>
              )}

              {/* ── ACTION BUTTONS — 2 columns in one row ── */}
              <div className="pd-actions pd-actions--row">
                <a
                  href={product.flipkartUrl || "https://www.flipkart.com"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-flipkart pd-btn"
                >
                  🛒 Buy on Flipkart
                </a>
                <a
                  href={`https://wa.me/919320905751?text=${encodeURIComponent(
                    `Hi, I want to enquire about ${product.name}`
                  )}`}
                  className="btn btn-wa pd-btn"
                  target="_blank"
                  rel="noreferrer"
                >
                  💬 Get Enquiry on WhatsApp
                </a>
              </div>

              <div className="pd-trust">
                <span>🚚 Pan-India Delivery</span>
                <span>🌿 Natural Ingredients</span>
                <span>✋ Handcrafted</span>
                <span>📍 Made in Mumbai</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── INFO CARDS ── */}
      <section className="pd-info-section" style={{ display: "block", width: "100%" }}>
        <div className="container">
          <div className="pd-info-grid">
          <div className="pd-info-card">
  <h3>🌿 Product Introduction</h3>
  <p className="pd-intro-text">
    {product.longDesc || product.desc || product.shortDesc}
  </p>

  {product.usage && (
    <>
      <h4 className="pd-subtitle">Recommended Usage</h4>
      <p className="pd-usage-text">{product.usage}</p>
    </>
  )}

  {/* ── PRODUCT BENEFITS ── */}
  {product.benefits && product.benefits.length > 0 && (
    <>
      <h4 className="pd-subtitle">Product Benefits</h4>
      <ul className="pd-benefits-list">
        {product.benefits.map((benefit, i) => (
          <li key={i} className="pd-benefit-item">
            <span className="pd-benefit-icon">✦</span>
            {benefit}
          </li>
        ))}
      </ul>
    </>
  )}
</div>

            <div className="pd-info-card">
              <h3>🔥 How to Use</h3>
              <ol className="pd-steps">
                {(product.howToUse || [
                  "Place agarbatti / dhoop in a suitable holder or stand.",
                  "Light the tip and allow to flame for 5–10 seconds.",
                  "Gently blow out — the tip should glow and emit smoke.",
                  "Place in a safe, ventilated area away from flammables.",
                  "Let the sacred fragrance fill your space.",
                  "Never leave burning incense unattended.",
                ]).map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
              <div className="pd-safety">
                <h4>⚠ Safety</h4>
                <p>
                  {product.safety ||
                    "Keep away from children and pets. Use in ventilated spaces. Store in a cool, dry place."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── RELATED PRODUCTS ── */}
      {related.length > 0 && (
        <section className="section">
          <div className="container">
            <div className="text-center reveal" style={{ marginBottom: "2.5rem" }}>
              <h2 className="h-display">Related Products</h2>
            </div>
            <div className="related-grid">
              {related.map((r) => {
                const rImgs = buildImagesArray(r);
                return (
                  <div key={r.id} className="p-card">
                    <Link to={`/products/${r.id}`}>
                      <div className="p-card-img">
                        <img src={rImgs[0]} alt={r.name} loading="lazy" />
                        {r.tier && <div className="p-card-tier">{r.tier}</div>}
                      </div>
                    </Link>
                    <div className="p-card-body">
                      <p className="p-card-frag">✦ {safeStr(r.fragrance)}</p>
                      <h3 className="p-card-name">{r.name}</h3>
                      {/* <p className="p-card-desc">{r.shortDesc || r.desc}</p> */}
                      <div className="p-card-info">
                        <span>⏱ {safeStr(r.duration || r.burnTime)}</span>
                        <span>⚖️ {safeStr(r.weight)}</span>
                      </div>
                      <div className="p-card-actions">
                        <Link to={`/products/${r.id}`} className="btn-card-view">
                          View Details
                        </Link>
                        <a
                          href={r.flipkartUrl || "https://www.flipkart.com"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-card-flipkart"
                          onClick={(e) => e.stopPropagation()}
                        >
                          🛒 Buy
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
